import styled, { keyframes } from 'styled-components';

import { fontStyles } from 'theme';
import ErrorMessage from 'components/ErrorMessage';

export const Container = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: calc(100% - 60px);
  width: 100%;
  background-color: var(--color-white);
  border-radius: var(--border-radius);
`;

export const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: var(--color-grey-f8);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color-grey-d8);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--color-grey-94);
  }
`;

export const Message = styled.div`
  display: flex;
  flex-direction: column;
  align-self: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  align-items: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  max-width: 90%;
  background-color: ${({ $isError }) => ($isError ? 'var(--color-pink)' : 'transparent')};
  border: ${({ $isError }) => ($isError ? '1px solid var(--color-red-f9)' : 'none')};
`;

export const MessageContent = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  padding: 12px 16px;
  border-radius: var(--border-radius);
  background-color: ${({ $isUser }) => ($isUser ? 'var(--color-primary)' : 'var(--color-grey-f8)')};
`;

export const AIText = styled.p`
  ${fontStyles.regularSmall}
  color: ${({ $isUser }) => ($isUser ? 'var(--color-white)' : 'var(--color-black)')};
  word-wrap: break-word;
  white-space: pre-wrap;
  line-height: 1.4;
  code {
    background-color: ${({ $isUser }) => ($isUser ? 'rgba(255, 255, 255, 0.2)' : 'var(--color-grey-e9)')};
    padding: 2px 4px;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
    font-size: 12px;
  }
  pre {
    background-color: ${({ $isUser }) => ($isUser ? 'rgba(255, 255, 255, 0.2)' : 'var(--color-grey-e9)')};
    padding: 8px;
    border-radius: 4px;
    overflow-x: auto;
    margin: 8px 0;
  }
  ul,
  ol {
    margin: 8px 0;
    padding-left: 20px;
  }
  li {
    margin: 4px 0;
  }
  a {
    color: ${({ $isUser }) => ($isUser ? 'rgba(255, 255, 255, 0.9)' : 'var(--color-primary)')};
    text-decoration: underline;

    &:hover {
      opacity: 0.8;
    }
  }
`;

export const MessageTime = styled.div`
  ${fontStyles.regularSmall}
  font-size: 11px;
  color: var(--color-grey-94);
  margin-top: 4px;
  margin-left: ${({ $isUser }) => ($isUser ? '0' : '8px')};
  margin-right: ${({ $isUser }) => ($isUser ? '8px' : '0')};
`;

export const ItemWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 24px 0 8px;
`;

export const InputContainer = styled.div`
  padding: 16px;
  background-color: var(--color-grey-f4);
  flex-shrink: 0;
`;

export const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background-color: var(--color-grey-fb);
  border: 1px solid var(--color-grey-e5);
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 0 7px rgba(0, 0, 0, 0.05);
  padding: 1px;
  width: 100%;
`;

export const TextAreaRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
  justify-content: flex-end;
  padding: 8px 12px;
  width: 100%;
`;

export const TextArea = styled.textarea`
  flex: 1;
  min-height: 40px;
  max-height: 192px;
  padding: 8px;
  border: none;
  border-radius: 0;
  font-size: 16px;
  font-family: inherit;
  resize: none;
  outline: none;
  background-color: transparent;
  color: var(--color-black);

  &::placeholder {
    color: var(--color-grey-73);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (min-width: 1024px) {
    font-size: 14px;
  }
`;

const spinAnimation = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

export const SendButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  min-width: 32px;
  height: 32px;
  background-color: var(--color-primary);
  border-radius: 8px;
  cursor: pointer;
  flex-shrink: 0;
  & > svg {
    width: 16px;
    height: 16px;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const SendSpinner = styled.span`
  display: block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spinAnimation} 0.7s linear infinite;
`;

export const ErrMessage = styled(ErrorMessage)`
  text-align: right;
`;

export const Disclaimer = styled.p`
  ${fontStyles.regularSmall}
  font-size: 11px;
  color: var(--color-grey-94);
  margin-top: 4px;
`;

const dotAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(20);
    opacity: 0.75;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;
const dotsAnimation = keyframes`
  0% {
    content: '';
  }
  25% {
    content: '.';
  }
  50% {
    content: '..';
  }
  75%,
  100% {
    content: '...';
  }
`;
export const Spinner = styled.div`
  display: grid;
  grid-template-columns: 16px 1fr;
  align-items: center;
  gap: 8px;
  &::before {
    content: '';
    width: 1px;
    min-width: 1px;
    height: 1px;
    min-height: 1px;
    border-radius: 50%;
    background-color: var(--color-black);
    animation: ${dotAnimation} 1.5s ease-in-out infinite;
    margin-left: 8px;
  }
`;

export const SpinnerText = styled.p`
  ${fontStyles.regularSmall}
  color: var(--color-black);
`;

export const AnimatedDots = styled.span`
  display: inline-block;
  width: 1.5em;
  text-align: left;
  &::after {
    content: '';
    animation: ${dotsAnimation} 1s steps(4, end) infinite;
  }
`;
